import { convertToISODateTimeString, DATE_FORMAT } from '@/helpers/dates';
import { PACKAGE_IN_REVIEW_QUEUE_ALERT_MESSAGE } from '@/helpers/packageEditability';
import { useToastSuccessSavedPackageMessage } from '@/hooks/useSavedPackageMessage';
import { isErrorWithData } from '@/services/helpers';
import { useSaveLoopFieldPackageMutation, useSubmitLoopFieldPackageMutation } from '@/services/loopFieldPackage';
import { Alert, Box, FormikTextInput, toast } from '@palmetto/palmetto-components';
import { Field, Formik, FormikValues } from 'formik';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import * as yup from 'yup';

import {
  GeothermalDesignDocumentTypes,
  GeothermalLoopFieldPackage,
  GeothermalSystem,
  LoopFieldPackageDocumentTypes,
  MilestoneType,
  PACKAGE_EDITABLE_QUEUE_STATUSES,
  PACKAGE_NOT_EDITABLE_QUEUE_STATUSES,
  QueueDocumentResponse,
  QueueStatus,
  QueueType,
} from 'types';

import { useGetAccountQuery } from '../../services/accounts';
import { useGetQueuesByAccountIdQuery } from '../../services/queues';
import { ReviewPackageActions } from '../../types/ReviewPackage';
import { ActionsList } from '../ActionsList';
import { useAuth } from '../auth/authProvider';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import { DesignDocumentsReview } from '../DesignDocuments/DesignDocumentsReview';
import {
  buildInitialValues,
  buildSystemDetails,
  hasCompleteSystemDetails,
  hasEnteredSystemDetails,
} from '../DesignDocuments/loopFieldSystemDetails';
import { EnhancedFormikForm } from '../EnhancedFormikForm';
import FormSection from '../Forms/FormSection';
import { MainContainer } from '../MainContainer';
import { DocumentList } from '../MilestonePackagePartials/Documents/DocumentList';
import { PhotosList } from '../MilestonePackagePartials/Documents/PhotosList';
import { Footer } from '../MilestonePackagePartials/Footer';
import { GeothermalSystemDetailsSummary } from '../MilestonePackagePartials/geothermal/GeothermalSystemDetailsSummary';
import { QueueBanner } from '../MilestonePackagePartials/QueueBanner';
import { NotesDrawer } from '../NotesDrawer/NotesDrawer';
import PageHeader from '../PageHeader';
import { GeothermalPermitSection } from '../Permits/GeothermalPermitSection';
import { QueueFlagsProvider } from '../QueueFlags/QueueFlagsContext';
import { SkeletonBox } from '../SkeletonBox';

interface LoopFieldPackageProps {
  canSubmitPackage: boolean;
}

const LoopFieldPackageSchema = yup.object().shape({
  drillerName: yup.string(),
  drillerLicense: yup.string(),
  systems: yup.array().of(
    yup.object().shape({
      equipment: yup.array().of(yup.object().shape({ model: yup.string().trim().required('Model is required.') })),
    }),
  ),
});

const EMPTY_CHECKBOX_CATEGORIES: any[] = [];

export const LoopFieldGeothermalPackage = ({ canSubmitPackage }: LoopFieldPackageProps) => {
  const navigate = useNavigate();
  const { claims } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);
  const { id = '', action } = useParams<{ id: string; action: ReviewPackageActions }>();
  const { data: queuesData, isLoading: isQueuesLoading } = useGetQueuesByAccountIdQuery({
    accountId: id,
    types: [QueueType.loopFieldPackage, QueueType.loopFieldDesignDocuments],
  });
  const queue = queuesData?.find((q: QueueDocumentResponse) => q.type === QueueType.loopFieldPackage);
  const designDocsQueue = queuesData?.find(
    (q: QueueDocumentResponse) => q.type === QueueType.loopFieldDesignDocuments,
  );

  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery(id, { skip: !id });
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [triggerSubmit] = useSubmitLoopFieldPackageMutation();
  const [triggerSave] = useSaveLoopFieldPackageMutation();
  const isLoading = isAccountLoading || isQueuesLoading;

  const showLoopFieldDocsForEPCUser = !isFinCoUser && queue?.status === QueueStatus.submitted;
  const triggerSuccessMessage = useToastSuccessSavedPackageMessage(id, MilestoneType.loopField);
  const canSubmitReview = action === ReviewPackageActions.review && queue;

  const showLoopFieldDocs =
    !isFinCoUser && (!queue || queue?.status === QueueStatus.submitted || queue?.status === QueueStatus.inProgress);

  const canEditPackage = isFinCoUser || !queue || PACKAGE_EDITABLE_QUEUE_STATUSES.has(queue.status);
  const showUnderReviewAlert = !isFinCoUser && queue && PACKAGE_NOT_EDITABLE_QUEUE_STATUSES.has(queue.status);

  const designDocsAreLocked = isFinCoUser
    ? false
    : [
        QueueStatus.inProgress,
        QueueStatus.reReview,
        QueueStatus.approved,
        QueueStatus.conditionallyApproved,
      ].includes(designDocsQueue?.status);

  const geothermalSystems: GeothermalSystem[] = account?.systemDesign?.systems ?? [];

  const subQueueInfo = designDocsQueue
    ? { type: QueueType.loopFieldDesignDocuments, status: designDocsQueue.status }
    : undefined;

  const accountInitialValues = useMemo(() => {
    if (!account) return {};
    return {
      drillerName: account.drillerName ?? '',
      drillerLicense: account.drillerLicense ?? '',
      permitSubmittedDate: account.permitSubmittedDate?.palmetto
        ? DateTime.fromISO(account.permitSubmittedDate.palmetto).toUTC().toFormat(DATE_FORMAT)
        : '',
      permitApprovedDate: account.permitApprovedDate?.palmetto
        ? DateTime.fromISO(account.permitApprovedDate.palmetto).toUTC().toFormat(DATE_FORMAT)
        : '',
    };
  }, [account]);

  const initialValues = useMemo(
    () => ({ ...accountInitialValues, ...buildInitialValues(account?.systemDesign?.systems ?? []) }),
    [accountInitialValues, account?.systemDesign?.systems],
  );

  // The stored design already carrying details means a blank field is a clear, not an absence.
  // `mergeSystemDetails` requires a model and cannot write an empty one, so a clear can never
  // persist and has to be refused rather than accepted and dropped.
  const storedDetailsExist = useMemo(
    () => hasEnteredSystemDetails(buildInitialValues(geothermalSystems)),
    [geothermalSystems],
  );

  const handleSubmit = useCallback(
    async (values: FormikValues, { setSubmitting }: any) => {
      try {
        setSubmitting(true);
        const { systems, ...packageValues } = values;
        // convertToISODateTimeString can return undefined; only build the { palmetto } object
        // once we have a real string, since ProjectDates requires string values.
        const permitSubmittedISO = packageValues.permitSubmittedDate
          ? convertToISODateTimeString(packageValues.permitSubmittedDate)
          : undefined;
        const permitApprovedISO = packageValues.permitApprovedDate
          ? convertToISODateTimeString(packageValues.permitApprovedDate)
          : undefined;
        await triggerSubmit({
          accountId: id,
          loopFieldPackage: {
            ...(packageValues as GeothermalLoopFieldPackage),
            permitSubmittedDate: permitSubmittedISO ? { palmetto: permitSubmittedISO } : undefined,
            permitApprovedDate: permitApprovedISO ? { palmetto: permitApprovedISO } : undefined,
          },
          // Unlike Save, Submit sends blanks too, so the server names what it rejected.
          ...(systems?.length ? { systemDetails: buildSystemDetails({ systems }) } : {}),
        }).unwrap();
        toast.success('Loop Field Package Submitted');
        navigate(`/accounts/${id}`);
      } catch (e: any) {
        toast.error(e?.data?.message || 'Error submitting loop field package');
      } finally {
        setSubmitting(false);
      }
    },
    [triggerSubmit, id, navigate],
  );

  const handleSave = useCallback(
    async ({ accountId, data }: { accountId: string; data: FormikValues; targetStatus?: QueueStatus }) => {
      const { systems, ...packageData } = data;

      // Three states, not two. Blank across the board with nothing stored means the reviewer is
      // here before Design Documents, so the block is omitted and the package saves. Complete
      // details are sent. Anything in between has to fail, because the server merge takes all
      // systems or none: omitting the block would drop the entry while reporting a saved package.
      const detailsComplete = hasCompleteSystemDetails({ systems });
      const detailsWouldBeLost = hasEnteredSystemDetails({ systems }) || storedDetailsExist;
      if (!detailsComplete && detailsWouldBeLost) {
        throw new Error('Model is required for every piece of equipment before system details can be saved.');
      }

      try {
        // convertToISODateTimeString can return undefined; only build the { palmetto } object
        // once we have a real string, since ProjectDates requires string values.
        const permitSubmittedISO = packageData.permitSubmittedDate
          ? convertToISODateTimeString(packageData.permitSubmittedDate)
          : undefined;
        const permitApprovedISO = packageData.permitApprovedDate
          ? convertToISODateTimeString(packageData.permitApprovedDate)
          : undefined;
        await triggerSave({
          accountId,
          savePackage: {
            ...packageData,
            permitSubmittedDate: permitSubmittedISO ? { palmetto: permitSubmittedISO } : undefined,
            permitApprovedDate: permitApprovedISO ? { palmetto: permitApprovedISO } : undefined,
          },
          ...(detailsComplete ? { systemDetails: buildSystemDetails({ systems }) } : {}),
        }).unwrap();
        triggerSuccessMessage();
      } catch (e) {
        console.error(e);
        if (isErrorWithData(e)) {
          throw new Error(e.data.message, { cause: e });
        }
        throw new Error('Error saving Loop Field Package', { cause: e });
      }
    },
    [triggerSave, triggerSuccessMessage, storedDetailsExist],
  );

  if (isLoading) {
    return (
      <MainContainer>
        <FormSection title="Loading...">
          <SkeletonBox height="25px" width="100" />
        </FormSection>
      </MainContainer>
    );
  }

  return (
    <QueueFlagsProvider queueId={queue?.id}>
      <Helmet>
        <title>{`${account?.primaryApplicantName ?? ''} Loop Field Package`}</title>
      </Helmet>
      <NotesDrawer isOpen={isNotesOpen} setIsOpen={setIsNotesOpen} queueType={QueueType.loopFieldPackage} />
      <MainContainer>
        <PageHeader
          eyebrow={
            <>
              <Link to="/accounts">Accounts</Link> \{' '}
              <Link to={`/accounts/${id}`}>{`${account?.primaryApplicantName ?? ''}`}</Link>
            </>
          }
          title={`Loop Field Package${account ? ': ' + account.primaryApplicantName : ''}`}
          description="Submit loop field installation documentation and permit details for review."
        />
        <Formik
          initialValues={initialValues}
          validationSchema={LoopFieldPackageSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ isSubmitting }) => (
            <EnhancedFormikForm>
              <ActionsList
                milestoneType={MilestoneType.loopField}
                accountId={queue?.account?.id}
                canSubmitPackage={canSubmitPackage}
                isLoading={isLoading}
                isFirstSubmission={!queue}
                canSubmitReview={canSubmitReview}
                onSave={handleSave}
                disabled={!canEditPackage}
                initialValues={initialValues}
                onSubmit={handleSubmit}
                queue={queue}
                subQueueInfo={subQueueInfo}
              />
              {queue && (queue.status === QueueStatus.rejected || isFinCoUser) ? (
                <QueueBanner
                  setIsNotesOpen={setIsNotesOpen}
                  queueType={QueueType.loopFieldPackage}
                  isFinCoUser={isFinCoUser}
                />
              ) : null}
              {showUnderReviewAlert && (
                <Alert
                  variant="warning"
                  title="Loop Field Package Under Review"
                  message={PACKAGE_IN_REVIEW_QUEUE_ALERT_MESSAGE}
                  hasIcon
                />
              )}
              <GeothermalPermitSection isDisabled={!canEditPackage} />
              <FormSection title="Driller Information">
                <Box direction={{ base: 'column', desktop: 'row' }} gap="xl">
                  <Box flex="auto">
                    <Field
                      label="Driller Name (optional)"
                      name="drillerName"
                      id="drillerName"
                      component={FormikTextInput}
                      isDisabled={isSubmitting || !canEditPackage}
                    />
                  </Box>
                  <Box flex="auto">
                    <Field
                      label="Driller License (optional)"
                      name="drillerLicense"
                      id="drillerLicense"
                      component={FormikTextInput}
                      isDisabled={isSubmitting || !canEditPackage}
                    />
                  </Box>
                </Box>
              </FormSection>

              {showLoopFieldDocs ? (
                <FormSection
                  title="Loop Field Documentation"
                  description="Upload all loop field documentation here. Internal review will categorize each document into the sections below. Required documentation: pressure test results, loop installation photos (ideally before backfill), drilling invoice (include bore depths/count for vertical or trench dimensions for horizontal), and bore log / as-built."
                >
                  <PhotosList
                    title="Loop Field Documentation"
                    description="Upload all loop field documentation — pressure test results, loop installation photos, drilling invoice, bore log / as-built"
                    category={'loopFieldDocumentation' as any}
                    baseCategory="Loop Field Documentation"
                    showCheckboxes={isFinCoUser}
                    checkboxCategories={isFinCoUser ? LoopFieldPackageDocumentTypes : EMPTY_CHECKBOX_CATEGORIES}
                    flagType={undefined as any}
                    programType={'geothermal' as any}
                    showUploadButton={canEditPackage}
                  />
                </FormSection>
              ) : (
                <>
                  {(isFinCoUser || showLoopFieldDocsForEPCUser) && (
                    <FormSection
                      title="Loop Field Documentation"
                      description="Upload all loop field documentation here. Internal review will categorize each document into the sections below. Required documentation: pressure test results, loop installation photos (ideally before backfill), drilling invoice (include bore depths/count for vertical or trench dimensions for horizontal), and bore log / as-built."
                    >
                      <PhotosList
                        title="Loop Field Documentation"
                        description="Upload all loop field documentation — pressure test results, loop installation photos, drilling invoice, bore log / as-built"
                        category={'loopFieldDocumentation' as any}
                        baseCategory="Loop Field Documentation"
                        showCheckboxes={isFinCoUser}
                        checkboxCategories={isFinCoUser ? LoopFieldPackageDocumentTypes : EMPTY_CHECKBOX_CATEGORIES}
                        flagType={undefined as any}
                        programType={'geothermal' as any}
                        showUploadButton={canEditPackage}
                      />
                    </FormSection>
                  )}
                  <FormSection
                    title="Categorized Documentation"
                    description="Documents categorized by internal review"
                  >
                    <PhotosList
                      title="Pressure Test Results"
                      description="Categorized by internal review"
                      category={'pressureTestResults' as any}
                      baseCategory="Loop Field Documentation"
                      showCheckboxes={isFinCoUser}
                      checkboxCategories={isFinCoUser ? LoopFieldPackageDocumentTypes : EMPTY_CHECKBOX_CATEGORIES}
                      flagType={undefined as any}
                      programType={'geothermal' as any}
                      showUploadButton={canEditPackage}
                      showIfEmpty={isFinCoUser}
                    />
                    <PhotosList
                      title="Loop Installation Photos"
                      description="Categorized by internal review"
                      category={'loopInstallationPhotos' as any}
                      baseCategory="Loop Field Documentation"
                      showCheckboxes={isFinCoUser}
                      checkboxCategories={isFinCoUser ? LoopFieldPackageDocumentTypes : EMPTY_CHECKBOX_CATEGORIES}
                      flagType={undefined as any}
                      programType={'geothermal' as any}
                      showUploadButton={canEditPackage}
                      showIfEmpty={isFinCoUser}
                    />
                    <PhotosList
                      title="Drilling Invoice"
                      description="Categorized by internal review"
                      category={'drillingInvoice' as any}
                      baseCategory="Loop Field Documentation"
                      showCheckboxes={isFinCoUser}
                      checkboxCategories={isFinCoUser ? LoopFieldPackageDocumentTypes : EMPTY_CHECKBOX_CATEGORIES}
                      flagType={undefined as any}
                      programType={'geothermal' as any}
                      showUploadButton={canEditPackage}
                      showIfEmpty={isFinCoUser}
                    />
                    <PhotosList
                      title="Bore Log / As-Built"
                      description="Categorized by internal review"
                      category={'boreLog' as any}
                      baseCategory="Loop Field Documentation"
                      showCheckboxes={isFinCoUser}
                      checkboxCategories={isFinCoUser ? LoopFieldPackageDocumentTypes : EMPTY_CHECKBOX_CATEGORIES}
                      flagType={undefined as any}
                      programType={'geothermal' as any}
                      showUploadButton={canEditPackage}
                      showIfEmpty={isFinCoUser}
                    />
                  </FormSection>
                </>
              )}
              {geothermalSystems.length > 0 && (
                <FormSection
                  title="System Details"
                  description="Model for each piece of equipment, so the design can be validated before install. Serial numbers are captured at Install."
                >
                  <GeothermalSystemDetailsSummary systems={geothermalSystems} isDisabled={!canEditPackage} />
                </FormSection>
              )}
              <FormSection
                title="Design Documents"
                description="Load calculation and equipment selection (Manual J & S). Loop Design report from approved tool."
              >
                <DesignDocumentsReview />
                <DocumentList
                  documentTypeFilter={Object.values(GeothermalDesignDocumentTypes)}
                  title="Design Documents"
                  showTypeLabelOnItems={true}
                  showDocumentActions={!designDocsAreLocked && canEditPackage}
                  showUploadButton={!designDocsAreLocked && canEditPackage}
                  baseCategory="designDocuments"
                />
              </FormSection>

              <Footer
                canSubmitPackage={canSubmitPackage}
                isLoading={isLoading}
                isFirstSubmission={!queue}
                canSubmitReview={canSubmitReview}
                onSave={handleSave}
                disabled={!canEditPackage}
                subQueueInfo={subQueueInfo}
              />
            </EnhancedFormikForm>
          )}
        </Formik>
      </MainContainer>
    </QueueFlagsProvider>
  );
};
